'use client';

import { Menu, MenuListType, NavItem } from '@/components/menu/Menu.tsx';
import { HeaderNotifications } from '@/lib/_components/HeaderNotifications/index.tsx';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { routeInfo, RouteInfoPageType, settingsPath } from '@/lib/_utils/routing.ts';
import { User } from '@supabase/supabase-js';
import useTranslation from 'next-translate/useTranslation';

interface HeaderNavProps {
	user: User;
}

const HeaderNav = (props: HeaderNavProps) => {
	const { user } = props;
	const { t } = useTranslation();
	const routerPathname = usePathname();
	const popoverOpenRef = useRef<'notification' | null>(null);
	const [popoverOpen, setPopoverOpen] = useState<'notification' | null>(null);

	function togglePopover(popover: 'notification' | null) {
		const toggleAction = popoverOpen === popover ? null : popover;
		setPopoverOpen(toggleAction);
		popoverOpenRef.current = toggleAction;
	}

	function retrieveRouteInfo(page: RouteInfoPageType) {
		return routeInfo({ t, page });
	}

	useEffect(() => {
		// Handler click
		const handleClickOutside = (event: MouseEvent) => {
			const isPopoverNotificationOpen = popoverOpenRef.current === 'notification';
			const popoverContent = document.querySelector('.nv-inbox__popoverContent');

			// Check if the click target is outside the notification popover
			if (popoverContent && !popoverContent.contains(event.target as Node) && isPopoverNotificationOpen) {
				setPopoverOpen(null);
				popoverOpenRef.current = null;
			}
		};

		// Add event listener
		document.addEventListener('click', handleClickOutside);

		// Cleanup to avoid memory leaks
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const menuListData: MenuListType[] = useMemo(
		() => [
			{
				id: '1',
				icon: 'homeIcon',
				iconActive: 'homeActiveIcon',
				label: retrieveRouteInfo('dashboard').label,
				path: retrieveRouteInfo('dashboard').path,
			},
			{
				id: '2',
				icon: 'candlestickIcon',
				iconActive: 'candlestickActiveIcon',
				label: retrieveRouteInfo('myTournaments').label,
				path: retrieveRouteInfo('myTournaments').path,
			},
			{
				id: '3',
				icon: 'calendarIcon',
				iconActive: 'calendarActiveIcon',
				label: retrieveRouteInfo('calendar').label,
				path: retrieveRouteInfo('calendar').path,
			},
		],
		[],
	);

	return (
		<nav className='flex'>
			<ul className='grid grid-flow-col gap-[12px]'>
				<Menu selected={routerPathname} menuList={menuListData} className={'lg:hidden'} />
				<li>
					<HeaderNotifications
						subscriberId={user.id}
						handleTriggerPopover={() => {
							togglePopover('notification');
						}}
						isPopoverOpen={popoverOpen === 'notification'}
					/>
				</li>
				<li className={'lg:hidden'}>
					<Link href={settingsPath} className={`block`}>
						<NavItem
							item={{
								id: '4',
								icon: 'settingsIcon',
								iconActive: 'settingsActiveIcon',
								label: t('common:settings'),
								path: settingsPath,
							}}
							selected={routerPathname === settingsPath}
						/>
					</Link>
				</li>
			</ul>
		</nav>
	);
};

export default HeaderNav;
